/**
 * Week maths for daily reward cycles.
 *
 * A cycle always covers exactly one Monday-to-Sunday week in UTC, and the server stores its start as
 * Monday 00:00:00.000Z. The CMS admin never types a week: they pick a Monday and the rest is derived.
 *
 * Everything here converts explicitly between the stored UTC instant and the local `Date` the MUI
 * pickers work with. Reading `getUTCDate()` off a picker value, or `getDate()` off a stored one, is
 * the timezone bug this module exists to prevent: east of UTC the picker's Monday reads back as the
 * previous Sunday, and west of UTC a stored Monday renders as the Sunday before it.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** The calendar day a stored UTC instant belongs to, as a local midnight `Date` the pickers accept. */
export const utcInstantToLocalDay = (value: string | Date): Date => {
  const d = new Date(value);

  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** The UTC midnight of the calendar day a picker returned. */
export const localDayToUtcInstant = (value: string | Date): Date => {
  const d = new Date(value);

  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

/**
 * Snaps a day the admin picked back to the Monday that opens its week, staying in local time
 * throughout. Use this on picker values only.
 */
export const toLocalMonday = (value: string | Date): Date => {
  const d = new Date(value);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayIndex = day.getDay(); // 0 = Sunday

  day.setDate(day.getDate() - (dayIndex === 0 ? 6 : dayIndex - 1));

  return day;
};

/**
 * The Monday 00:00 UTC that opens the given instant's week — the server's cycle start, and the
 * week boundary the whole feature runs on. Stays in UTC throughout: reading local parts here is
 * what makes a stored Monday look like the previous Sunday, and therefore the previous week,
 * anywhere west of UTC.
 */
export const toUtcMonday = (value: string | Date): Date => {
  const d = new Date(value);
  const utcMidnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dayIndex = new Date(utcMidnight).getUTCDay(); // 0 = Sunday

  return new Date(utcMidnight - (dayIndex === 0 ? 6 : dayIndex - 1) * DAY_MS);
};

/** Stable key for a week, so taken weeks can be compared without instant arithmetic. */
export const weekKey = (value: string | Date): string =>
  toUtcMonday(value).toISOString().slice(0, 10);

export const currentWeekStart = (now: Date = new Date()): Date =>
  toUtcMonday(now);

export const addWeeks = (start: string | Date, weeks: number): Date =>
  new Date(new Date(start).getTime() + weeks * 7 * DAY_MS);

/** The Sunday that closes the week a start date opens. Display only: the server derives its own. */
export const weekEnd = (start: string | Date): Date =>
  new Date(toUtcMonday(start).getTime() + 6 * DAY_MS);

/**
 * The week a new cycle should default to: this week if it is still free, otherwise the first later
 * week that is. Weeks the admin deliberately skipped stay skipped — those fall back to the default
 * cycle, which is exactly what skipping one is for.
 */
export const nextFreeWeekStart = (
  takenWeeks: string[],
  now: Date = new Date(),
): Date => {
  const taken = new Set(takenWeeks);
  let candidate = currentWeekStart(now);

  // 520 weeks is ten years out; a CMS that has every week booked that far ahead is not a real state,
  // and the bound keeps a bad `takenWeeks` from spinning forever.
  for (let i = 0; i < 520; i += 1) {
    if (!taken.has(weekKey(candidate))) return candidate;
    candidate = addWeeks(candidate, 1);
  }

  return candidate;
};

/** Formats a stored UTC instant as a calendar day, never shifted by the viewer's timezone. */
export const formatUtcDay = (
  value?: string | Date | null,
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  },
): string => {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-GB", {
    ...options,
    timeZone: "UTC",
  });
};

/** "10 Aug – 16 Aug 2026", the way a week reads in the list and the editor header. */
export const formatWeekRange = (start?: string | Date | null): string => {
  if (!start) return "—";

  const from = formatUtcDay(toUtcMonday(start), {
    day: "2-digit",
    month: "short",
  });

  return `${from} – ${formatUtcDay(weekEnd(start))}`;
};
